import { useMemo, useEffect } from 'react';
import { useNavigate, useI18n, useQueryState } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { Award } from 'lucide-react';
import { SelectNetwork } from '@/components/common/SelectNetwork';
import { SelectToken } from '@/components/common/SelectToken';
import { type FormElement, Form, validator } from '@/lib/react-form';
import { useToken } from '@/hooks/useToken';
import { useConnectedGuard } from '@/hooks/useConnectedGuard';
import { useUserBalance } from '@/hooks/useUserBalance';
import { useWithdrawForm } from '@/hooks/useWithdrawForm';
import { BADGE_LEVELS, MOONINI_TOKEN_ID, MIN_WITHDRAW_MOONINI, MOONINI_DECIMALS } from '@/constants';
import * as schema from '@/db/schema';
import * as utils from '@/utils';
import * as web3 from '@ywwwtseng/web3';
import BigNumber from 'bignumber.js';

export default function WithdrawScreen({
  params,
}: {
  params: { token_id?: string };
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const form = useWithdrawForm(params.token_id);
  const token = useToken(form.values.token_id as string | undefined);
  const me = useQueryState<typeof schema.users.$inferSelect>('me');

  const navigateToWithdrawScreen = useConnectedGuard(
    () => {
      navigate('WithdrawConfirm', {
        params: { token_id: params.token_id },
        type: 'replace',
      });
    },
    { verified: true, network: token?.network }
  );

  const balance = useUserBalance(token);

  const badgeLevel = useMemo(() => {
    if (!me?.badge_level) return 1;
    return me.badge_level as keyof typeof BADGE_LEVELS;
  }, [me?.badge_level]);

  const badgeInfo = useMemo(() => {
    return BADGE_LEVELS[badgeLevel] || BADGE_LEVELS[1];
  }, [badgeLevel]);

  const badgeName = useMemo(() => {
    return t(`common.badge_level_${badgeLevel}` as any) || badgeInfo.name;
  }, [badgeLevel, badgeInfo.name, t]);

  const elements = useMemo<{ row: FormElement[] }[]>(() => {
    const values = form.values;

    return [
      {
        row: [
          {
            ui: 'custom',
            name: 'network',
            validate: validator.required({
              message: t('validation.required', {
                name: t('common.network'),
              }),
            }),
            render: ({ setValue }) => (
              <SelectNetwork
                network={values.network as string | null}
                onNetworkSelected={(network) => {
                  setValue('network', network);
                }}
              />
            ),
          },
        ],
      },
      {
        row: [
          {
            ui: 'custom',
            name: 'token_id',

            dependencies: ['network'],

            validate: validator.required({
              message: t('validation.required', {
                name: t('common.token'),
              }),
            }),
            render: ({ values, setValue }) => {
              return (
                <SelectToken
                  disabled={!values.network}
                  network={values.network as string}
                  canChangeNetwork={false}
                  canAddToken={false}
                  value={values.token_id as string | null}
                  onChange={(value) => {
                    setValue('token_id', value);
                  }}
                />
              );
            },
          },
        ],
      },
      {
        row: [
          {
            ui: 'amount',
            label: t('withdraw.amount'),
            dependencies: ['network', 'token'],
            name: 'amount',
            validate: [
              validator.gt(0, {
                message: t('validation.gt', {
                  name: t('withdraw.amount'),
                  value: 0,
                }),
              }),
              (value: unknown) => {
                if (
                  value &&
                  token &&
                  utils.token.isInsufficient({
                    balance: balance.value,
                    amount: value as string,
                    decimals: token.decimals,
                  })
                ) {
                  return t('common.insufficient_balance');
                }
              },
              (value: unknown) => {
                // 检查 $MOONINI 最低提领限制
                if (value && token && token.id === MOONINI_TOKEN_ID) {
                  const amountBN = new BigNumber(value as string);
                  const minAmountBN = new BigNumber(web3.utils.formatUnits(MIN_WITHDRAW_MOONINI.toString(), MOONINI_DECIMALS));
                  if (amountBN.lt(minAmountBN)) {
                    return t('message.withdraw_moonini_minimum', {
                      min_amount: minAmountBN.toString(),
                    });
                  }
                }
              },
            ],
            validateType: 'onChange',
            description: token
              ? balance.hasLocked
                ? t('common.available_balance', {
                  balance: balance.formatted,
                }) + ' ' + t('common.balance_locked_hint', {
                  locked: balance.lockedFormatted,
                })
                : t('common.available_balance', {
                  balance: balance.formatted,
                })
              : null,
            props: {
              disabled: !token,
              className:
                values.amount &&
                  token &&
                  utils.token.isInsufficient({
                    balance: balance.value,
                    amount: values.amount as string,
                    decimals: token.decimals,
                  })
                  ? 'text-destructive'
                  : '',
              decimal: token?.decimals ?? 0,
            },
          },
        ],
      },
    ];
  }, [form.values, token, balance]);

  useEffect(() => {
    return () => {
      form.reset();
    };
  }, []);

  return (
    <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
      {me && (
        <div className="card flex flex-col w-full gap-2 !justify-start mb-2">
          <div className="h-stack w-full gap-2 items-center">
            <Award size={20} className="text-primary" />
            <Typography size="4" weight={600}>
              {t('common.badge', { level: badgeLevel, name: badgeName }) || `Lv${badgeLevel} ${badgeName}`}
            </Typography>
          </div>
          <div className="v-stack w-full gap-1 pl-7">
            <Typography size="2" color="rgba(255,255,255,0.7)">
              {t('withdraw.badge_daily_count', { count: badgeInfo.dailyWithdrawCount }) || `Daily Withdrawals: ${badgeInfo.dailyWithdrawCount} times`}
            </Typography>
            <Typography size="2" color="rgba(255,255,255,0.7)">
              {t('withdraw.badge_single_limit', { amount: badgeInfo.maxSingleWithdrawUsd }) || `Single Withdrawal Limit: ${badgeInfo.maxSingleWithdrawUsd} USD`}
            </Typography>
            <Typography size="2" color="rgba(255,255,255,0.7)">
              {t('withdraw.badge_daily_total_limit', { amount: badgeInfo.maxDailyWithdrawUsd }) || `Daily Total Limit: ${badgeInfo.maxDailyWithdrawUsd} USD`}
            </Typography>
          </div>
        </div>
      )}
      <Form
        {...form}
        elements={elements}
        name="withdraw"
        submit={{
          label: t('common.withdraw'),
        }}
        onSubmit={navigateToWithdrawScreen}
      />
    </div>
  );
}
